import { useMemo } from "react";
import useLocale from "providers/LocaleProvider/useLocale";

/**
 * Why cache collators?
 * Collators are expensive to create, so we cache them to avoid creating new instances
 * every time a component re-renders. This is especially important in components that
 * filter or sort large lists of items.
 */
const cache = new Map<string, Intl.Collator>();

/**
 * Provides localized string collation for the current locale. Automatically updates when the locale changes,
 * and handles caching of the collator for performance.
 *
 * It is useful for sorting items in a list and can be used in conjunction with thenBy lib cmp option.
 * @see https://www.npmjs.com/package/thenby#internationalization-using-javascripts-native-intlcollator
 */
function useCollator(options?: Intl.CollatorOptions): Intl.Collator {
  const { locale } = useLocale();

  return useMemo(() => {
    const collatorOptions: Intl.CollatorOptions = { sensitivity: "base", usage: "sort", ...options };

    /** Make sure the options are sorted */
    const stringifiedOptions = Object.entries(collatorOptions)
      .sort((a, b) => (a[0] < b[0] ? -1 : 1))
      .join();

    const cacheKey = [locale, stringifiedOptions].join(":");
    if (cache.has(cacheKey)) {
      return cache.get(cacheKey) as Intl.Collator;
    }
    return new Intl.Collator(locale, collatorOptions);
  }, [locale, options]);
}

export default useCollator;
